import { computed, toValue, type MaybeRefOrGetter } from 'vue';

type UseInputWrapperClassesOptions = {
  disabled: MaybeRefOrGetter<boolean>;
  hasError: MaybeRefOrGetter<boolean>;
  variant?: MaybeRefOrGetter<'title' | 'default' | 'onSurface'>;
};

/**
 * Shared ring / disabled / error classes for input-like wrappers.
 *
 * Usage:
 * const wrapperClasses = useInputWrapperClasses({
 *   disabled: computed(() => props.disabled),
 *   hasError: computed(() => !!errorMessage.value),
 * });
 */
export function useInputWrapperClasses(options: UseInputWrapperClassesOptions) {
  const { disabled, hasError, variant } = options;

  return computed(() => {
    if (toValue(disabled)) {
      return [
        'bg-neutral-200',
        'opacity-50',
        'ring-neutral-200',
        'focus-within:ring-neutral-200',
        'hover:ring-neutral-200',
        'pointer-events-none',
      ];
    }

    if (toValue(hasError)) {
      return [
        'bg-neutral-100',
        'ring-error',
        'focus-within:ring-error',
        'hover:ring-error',
      ];
    }

    if (toValue(variant) === 'title') {
      return [
        'bg-transparent',
        'ring-transparent',
        'focus-within:ring-transparent',
        'hover:ring-transparent',
        'focus-within:bg-neutral-200',
        'md:h-11',
      ];
    }

    if (toValue(variant) === 'onSurface') {
      return [
        'bg-neutral-0',
        'ring-neutral-200',
        'focus-within:ring-vp-primary-300',
        'hover:ring-vp-primary-300',
      ];
    }

    return [
      'bg-neutral-100',
      'ring-neutral-200',
      'focus-within:ring-vp-primary-300',
      'hover:ring-vp-primary-300',
    ];
  });
}
